import { isSameDay } from "date-fns";
import { v4 as uuidv4 } from "uuid";
import { IconTypes } from "../components/ui/icons";
import { darkVistaBlue, olivineColor, roseOrange } from "../styles/theme";
import {
  ActivityExtraProperties,
  ActivityResponse,
  ActivityType,
  EventExtraProperties,
  EventResponse,
  EventType,
} from "../types/actionPlan";
import { ProtectedRoutes } from "../utils/routes.utils";

const getEventIcon = (eventType: EventType): IconTypes | undefined => {
  if (eventType === EventType.SurveyReminder) {
    return;
  }
  if (eventType === EventType.Touchpoint) {
    return IconTypes.GraduationHat;
  }
  if (
    eventType === EventType.SurveyLaunch ||
    eventType === EventType.ScheduleSurveyLaunch
  ) {
    return IconTypes.Clipboard;
  }
  if (eventType === EventType.SurveyDueDate) {
    return IconTypes.Flag;
  }
};

export class Event {
  id: number | undefined;
  activityId: number | undefined;
  eventType: EventType;
  date: Date;
  label: string;
  extraProperties: EventExtraProperties;

  // Optional UI fields
  icon?: IconTypes;
  iconColor?: string;

  constructor(apiResponse: EventResponse) {
    this.id = apiResponse.id;
    this.activityId = apiResponse.activity_id;
    this.eventType = apiResponse.event_type;
    this.date = new Date(apiResponse.date);
    this.label = apiResponse.label;
    this.extraProperties = apiResponse.extra_properties;
  }

  toAPI(): EventResponse {
    return {
      id: this.id,
      activity_id: this.activityId,
      event_type: this.eventType,
      date: this.date.toISOString(),
      label: this.label,
      extra_properties: this.extraProperties,
    };
  }

  setUIFields(activityColor: string, isOnlyEvent: boolean): this {
    this.icon = getEventIcon(this.eventType);
    this.iconColor = isOnlyEvent ? "white" : activityColor;
    return this;
  }
}

export class Activity {
  id: number | undefined;
  clientId: string; // Client-side id
  activityType: ActivityType;
  label: string;
  startDate: Date;
  endDate: Date | undefined;
  events: Event[];
  extraProperties: ActivityExtraProperties;

  // UI fields
  color?: string;
  href?: string;

  constructor(apiResponse: ActivityResponse) {
    this.id = apiResponse.id;
    this.clientId = uuidv4();
    this.activityType = apiResponse.activity_type;
    this.label = apiResponse.label;
    this.startDate = new Date(apiResponse.start_date);
    this.endDate = apiResponse.end_date
      ? new Date(apiResponse.end_date)
      : undefined;
    this.events = apiResponse.events.map((e) => new Event(e));
    this.extraProperties = apiResponse.extra_properties;
  }

  toAPI(): ActivityResponse {
    return {
      id: this.id,
      activity_type: this.activityType,
      label: this.label,
      start_date: this.startDate.toISOString(),
      end_date: this.endDate?.toISOString(),
      events: this.events.map((e) => e.toAPI()),
      extra_properties: this.extraProperties,
    };
  }

  setUIFields(): this {
    this.color = this.getColor();
    this.href = this.getHref();
    const isOnlyEvent = this.events.length === 1;
    this.events.forEach((event) => event.setUIFields(this.color!, isOnlyEvent));
    return this;
  }

  updateEventDate(eventIndex: number, newDate: Date): Activity {
    const updatedEvents = this.events.map((event, index) => {
      if (index === eventIndex) {
        const updatedEvent = new Event(event.toAPI());
        updatedEvent.date = newDate;
        return updatedEvent;
      }
      return event;
    });

    const updatedActivity = new Activity(this.toAPI());
    updatedActivity.clientId = this.clientId;
    updatedActivity.events = updatedEvents;

    // Recalculate startDate and endDate based on updated events
    const eventDates = updatedEvents.map((e) => e.date.getTime());
    updatedActivity.startDate = new Date(Math.min(...eventDates));
    // Start and end date should be the same if there's only one event
    if (updatedEvents.length === 1) {
      updatedActivity.endDate = updatedActivity.startDate;
    } else {
      updatedActivity.endDate = new Date(Math.max(...eventDates));
    }

    return updatedActivity;
  }

  validateEventsOrder(): boolean {
    const orderedEvents = this.events.filter(
      (e) => e.extraProperties?.touchpoint?.order !== undefined
    );

    if (orderedEvents.length <= 1) return true;

    // Sort by touchpoint order, then check if dates are ascending
    const sortedByOrder = [...orderedEvents].sort(
      (a, b) =>
        a.extraProperties.touchpoint!.order! -
        b.extraProperties.touchpoint!.order!
    );

    return sortedByOrder.every(
      (event, i) => i === 0 || event.date > sortedByOrder[i - 1].date
    );
  }

  private getColor(): string {
    if (this.activityType === ActivityType.Training) {
      return darkVistaBlue;
    } else if (this.activityType === ActivityType.LeadershipTraining) {
      return olivineColor;
    }
    return roseOrange;
  }

  private getHref(): string | undefined {
    if (this.isTrainingActivity()) {
      return ProtectedRoutes.TRAINING;
    }
    if (this.activityType === ActivityType.Survey) {
      const dispatchId = this.extraProperties?.dispatch?.id;
      if (dispatchId) {
        return `${ProtectedRoutes.LAUNCH}/${dispatchId}`;
      }
    }
    return undefined;
  }

  isTrainingActivity(): boolean {
    return (
      this.activityType === ActivityType.Training ||
      this.activityType === ActivityType.LeadershipTraining
    );
  }

  isSurveyActivity(): boolean {
    return (
      this.activityType === ActivityType.Survey ||
      this.activityType === ActivityType.ScheduleSurvey
    );
  }

  isEditable(now: Date): boolean {
    // Closed surveys are not editable
    if (this.endDate && this.activityType === ActivityType.Survey) {
      return this.endDate > now;
    }
    // Training activities are always editable
    return true;
  }

  getLayout(
    timelineStart: Date,
    timelineEnd: Date
  ): { left: string; width?: string } {
    const left = `${this.getPositionFromDate(
      this.startDate,
      timelineStart,
      timelineEnd
    )}%`;

    const isSameDayRange =
      this.endDate && isSameDay(this.startDate, this.endDate);

    const width =
      this.endDate && !isSameDayRange
        ? `${Math.max(
            this.getPositionFromDate(this.endDate, timelineStart, timelineEnd) -
              parseFloat(left),
            2
          )}%`
        : undefined;

    return { left, width };
  }

  private getPositionFromDate(date: Date, start: Date, end: Date): number {
    const totalRange = end.getTime() - start.getTime();
    const elapsed = date.getTime() - start.getTime();
    return (elapsed / totalRange) * 100;
  }
}
